import { useEffect, useState } from 'react'
import { Button, Col, Container, Form, Modal, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import KLayerNavigation from '../components/KLayerNavigation'
import { apiRequest, isUserLoggedIn, showKAlert, updateAccessName } from '../helpers'
import { t } from '../locale'
import { ServiceUsageLog, User } from '../react-app-env'
import v from '../validators'
import KConfirm from '../components/KConfirm'
import { getServiceLabels } from '../helpers/services.helper'
import ServiceOverviewTable from '../components/ServiceOverviewTable'
import {
  disconnectMicrosoft,
  isMicrosoftEnabled,
  startMicrosoftConnect,
} from '../service/microsoft'
import {
  getSharePointRootItems,
  getSharePointSite,
  saveSharePointSite,
} from '../service/sharepoint'

export default function UserProfile() {
  const navigate = useNavigate()
  const [user, setUser] = useState({} as User)

  const [faEnabled, setFaEnabled] = useState(false)
  const [faCurrentEnable, setFaCurrentEnable] = useState(false)

  const [gender, setGender] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const [isMain, setIsMain] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [usageHistory, setUsageHistory] = useState<ServiceUsageLog[]>([])
  const [isActive, setIsActive] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [pinCodeEmail, setPinCodeEmail] = useState(true)
  const [pinCodeSms, setPinCodeSms] = useState(false)

  const [isGenderValid, setIsGenderValid] = useState(true)
  const [isFirstNameValid, setIsFirstNameValid] = useState(true)
  const [isLastNameValid, setIsLastNameValid] = useState(true)
  const [isEmailValid, setIsEmailValid] = useState(true)
  const [isPhoneValid, setIsPhoneValid] = useState(true)

  const [qr, setQr] = useState('')
  const [manualKey, setManualKey] = useState('')
  const [code, setCode] = useState('')

  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])

  const [confirmDeleteCompany, setConfirmDeleteCompany] = useState(false)

  const [microsoftConnected, setMicrosoftConnected] = useState(false)
  const [microsoftUserName, setMicrosoftUserName] = useState('')
  const [microsoftDisplayName, setMicrosoftDisplayName] = useState('')
  const [microsoftTenantId, setMicrosoftTenantId] = useState('')
  const [microsoftLoading, setMicrosoftLoading] = useState(false)
  const [sharePointSiteDisplayName, setSharePointSiteDisplayName] = useState('')
  const [sharePointUrl, setSharePointUrl] = useState('')
  const [sharePointSiteSaving, setSharePointSiteSaving] = useState(false)
  const [microsoftEnabled, setMicrosoftEnabled] = useState(true)

  useEffect(() => {
    apiRequest(`${process.env.REACT_APP_API_URL}/user/get`, 'POST').then(r => {
      if (!!r.user) {
        const user = r.user as User

        setUser(user)

        setIsMain(user.permissions.main)

        setGender(user.gender)
        setIsGenderValid(v.gender(user.gender))

        setFirstName(user.firstName)
        setIsFirstNameValid(v.name(user.firstName))

        setLastName(user.lastName)
        setIsLastNameValid(v.name(user.lastName))

        setEmail(user.email)
        setIsEmailValid(v.email(user.email))

        setPhone(user.phone)
        setIsPhoneValid(v.phone(user.phone))

        setIsActive(user.active)
        setIsConfirmed(user.confirmed)
        setIsAdmin(user.permissions.admin)

        setPinCodeEmail(user.pinCodeOptions.email)
        setPinCodeSms(user.pinCodeOptions.sms)

        if (!!user.twoFA) {
          setFaEnabled(user.twoFA.enabled)
        }
      }
    })

    apiRequest(`${process.env.REACT_APP_API_URL}/twofa/begin`, 'GET').then(d => {
      if (!!d.qrDataUrl) {
        setQr(d.qrDataUrl)
        setManualKey(d.manualKey)
      }
    })

    isMicrosoftEnabled().then(setMicrosoftEnabled)

    apiRequest(`${process.env.REACT_APP_API_URL}/microsoft/connection`, 'GET').then(r => {
      setMicrosoftConnected(!!r.connected)

      if (r.connection) {
        setMicrosoftUserName(r.connection.userPrincipalName ?? '')
        setMicrosoftDisplayName(r.connection.displayName ?? '')
        setMicrosoftTenantId(r.connection.tenantId ?? '')
      }
    })

    getSharePointSite().then(r => setSharePointSiteDisplayName(r.site?.displayName ?? ''))
  }, [])

  useEffect(() => {
    if (!isMain) return

    fetch(`${process.env.REACT_APP_API_URL}/subscription/service-usage/history`)
      .then(r => r.json())
      .then(r => {
        if (Array.isArray(r)) setUsageHistory(r)
      })
  }, [isMain])

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL

    if (!apiUrl) return

    const callbackUrl = new URL(apiUrl)

    if (callbackUrl.protocol === 'http:') {
      callbackUrl.hostname = 'localhost'
    }

    function handleMicrosoftMessage(event: MessageEvent) {
      if (event.origin !== callbackUrl.origin) return

      const result = event.data

      if (!result || result.source !== 'ds-kyc-microsoft') return

      setMicrosoftLoading(false)

      if (result.error) {
        showKAlert('danger', result.error)
        return
      }

      if (result.connected) {
        setMicrosoftConnected(true)
        showKAlert('success', 'microsoftConnectedSuccessfully')

        apiRequest(`${process.env.REACT_APP_API_URL}/microsoft/connection`, 'GET').then(r => {
          if (r.connection) {
            setMicrosoftUserName(r.connection.userPrincipalName ?? '')
            setMicrosoftDisplayName(r.connection.displayName ?? '')
            setMicrosoftTenantId(r.connection.tenantId ?? '')
          }
        })
      }
    }

    window.addEventListener('message', handleMicrosoftMessage)

    return () => {
      window.removeEventListener('message', handleMicrosoftMessage)
    }
  }, [])

  const serviceLabels: Record<string, string> = getServiceLabels()

  function update(userData: any) {
    fetch(`${process.env.REACT_APP_API_URL}/user-admin`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    })
      .then(r => r.json())
      .then(r => {
        if (r.ok) {
          showKAlert('success', 'userUpdated')
          updateAccessName(userData)

          navigate('/user')
        }
      })
  }
  function handleGender(e: any) {
    setGender(e.target.value)
    setIsGenderValid(v.gender(e.target.value))
  }
  function handleFirstName(e: any) {
    setFirstName(e.target.value.trimStart())
    setIsFirstNameValid(v.name(firstName))
  }
  function handleLastName(e: any) {
    setLastName(e.target.value.trimStart())
    setIsLastNameValid(v.name(lastName))
  }
  function handleEmail(e: any) {
    setEmail(e.target.value.trimStart())
    setIsEmailValid(v.email(email))
  }
  function handlePhone(e: any) {
    setPhone(e.target.value.trimStart())
    setIsPhoneValid(v.phone(phone))
  }
  function handlePincodeEmail(e: any) {
    setPinCodeEmail(e.target.checked)
  }
  function handleSubmit(e: any) {
    e.preventDefault()

    setIsGenderValid(v.gender(gender))
    setIsFirstNameValid(v.name(firstName))
    setIsLastNameValid(v.name(lastName))
    setIsEmailValid(v.email(email))
    setIsPhoneValid(v.phone(phone))

    if (
      v.gender(gender) &&
      v.name(firstName) &&
      v.name(lastName) &&
      v.email(email) &&
      v.phone(phone) &&
      (pinCodeEmail || pinCodeSms)
    ) {
      const userData = {
        _id: user._id,
        gender: gender,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        active: isActive,
        confirmed: isConfirmed,
        permissions: {
          admin: isAdmin,
          main: isMain,
        },
        pinCodeOptions: {
          email: pinCodeEmail,
          sms: pinCodeSms,
        },
      }

      update(userData)
    }
  }
  function handleChangeTwoFA(enabled: boolean) {
    apiRequest(`${process.env.REACT_APP_API_URL}/user`, 'PUT', {
      twoFA: {
        enabled: enabled,
      },
    }).then(r => {
      if (!r.ok) showKAlert('danger', 'Not changed. Server error.')

      window.location.reload()
      showKAlert('success', 'Success')
    })
  }
  async function confirm() {
    apiRequest(`${process.env.REACT_APP_API_URL}/twofa/confirm`, 'POST', { token: code }).then(
      r => {
        if (r.ok) {
          setRecoveryCodes(r.recoveryCodes || [])
          setShowRecoveryCodes(true)
          setFaEnabled(true)
          setFaCurrentEnable(false)
          setCode('')
        }
      }
    )
  }
  function handleCloseRecoveryCodes() {
    setShowRecoveryCodes(false)
    window.location.reload()
  }

  function copyRecoveryCodes() {
    const codesText = recoveryCodes.join('\n')

    const textArea = document.createElement('textarea')
    textArea.value = codesText
    document.body.appendChild(textArea)
    textArea.select()

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(codesText)
          .then(() => {
            alert('Recovery codes copied to clipboard!')
          })
          .catch(err => {
            alert('Recovery codes copied!')
          })
      } else {
        alert('Recovery codes copied!')
      }
    } catch (err) {
      console.error('Failed to copy: ', err)
      alert('Please select and copy the codes manually')
    } finally {
      document.body.removeChild(textArea)
    }
  }

  function downloadRecoveryCodes() {
    if (!recoveryCodes.length) return

    const content = recoveryCodes.join('\n')
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' })

    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = 'recovery-codes.txt'

    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  function deleteCompany() {
    setConfirmDeleteCompany(false)
    fetch(`${process.env.REACT_APP_API_URL}/company-admin`, {
      method: 'DELETE',
    })
      .then(r => r.json())
      .then(r => {
        if (r.ok) {
          navigate('/user/logout')
        }
      })
  }

  async function connectMicrosoft() {
    if (!process.env.REACT_APP_API_URL) {
      showKAlert('danger', 'microsoftAuthenticationFailed')
      return
    }

    const popup = window.open('', 'microsoftConnect', 'width=600,height=700')

    if (!popup) {
      showKAlert('danger', 'microsoftPopupBlocked')
      return
    }

    setMicrosoftLoading(true)

    try {
      const result = await startMicrosoftConnect()

      if (!result.authorizationUrl) {
        popup.close()
        setMicrosoftLoading(false)
        showKAlert('danger', 'microsoftAuthenticationFailed')
        return
      }

      popup.location.href = result.authorizationUrl
    } catch {
      popup.close()
      setMicrosoftLoading(false)
      showKAlert('danger', 'microsoftAuthenticationFailed')
    }
  }

  async function disconnectMicrosoftAccount() {
    const result = await disconnectMicrosoft()

    if (result.ok) {
      setMicrosoftConnected(false)
      setMicrosoftUserName('')
      setMicrosoftDisplayName('')
      setMicrosoftTenantId('')
      showKAlert('success', 'microsoftDisconnected')
    }
  }

  async function testMicrosoftConnection() {
    const result = await getSharePointRootItems()

    if (Array.isArray(result.items)) {
      showKAlert('success', 'microsoftConnectionOk')
    }
  }

  function parseUrl(value: string): { hostname: string; relativePath: string } | null {
    try {
      const url = new URL(value.trim())
      const relativePath = url.pathname.replace(/^\/+/, '')

      if (!url.hostname || !relativePath) return null

      return { hostname: url.hostname, relativePath }
    } catch {
      return null
    }
  }

  async function saveSharePointSiteConfig() {
    const parsed = parseUrl(sharePointUrl)

    if (!parsed) {
      showKAlert('danger', 'sharePointSiteInvalid')
      return
    }

    setSharePointSiteSaving(true)

    try {
      const result = await saveSharePointSite(parsed)

      if (result.site) {
        setSharePointSiteDisplayName(result.site.displayName)
        setSharePointUrl('')
        showKAlert('success', 'sharePointSiteSaved')
      }
    } finally {
      setSharePointSiteSaving(false)
    }
  }

  return (
    <>
      <Container>
        <Row>
          <div className='sidebar col-md-3 col-lg-2 p-0'>
            <KLayerNavigation logged={isUserLoggedIn()} />
          </div>
          <main className='ms-sm-auto col-lg-10 px-md-4 mb-4'>
            <div className='col-12'>
              <p className='pe-none text-draft lead fw-bold link-offset-3 link-underline-primary'>
                {t('profile')}
              </p>
            </div>

            <div className='col-12'>
              <div className='my-5 md:grid md:grid-cols-3 md:gap-6'>
                <Row>
                  <Col lg={4}>
                    <div className='d-flex justify-between section-title-blade'>
                      <div className='pe-4 sm:px-0'>
                        <h6 className='text-draft fw-bold'>{t('profileInformation')}</h6>
                        <p className='mt-1 text-draft'>{t('updateProfile')}</p>
                      </div>
                    </div>
                  </Col>
                  <Col lg={8}>
                    <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                      <div className='grid'>
                        <Row>
                          <Col>
                            <label className='fw-medium smaller text-primary required'>
                              {t('gender')}
                            </label>
                            <Form.Select
                              className='w-75 border-primary'
                              id='salutation'
                              value={gender}
                              onChange={handleGender}
                              isInvalid={!isGenderValid}
                            >
                              <option value=''>--</option>
                              <option value='male'>{t('male')}</option>
                              <option value='female'>{t('female')}</option>
                              <option value='other'>{t('other')}</option>
                            </Form.Select>
                          </Col>
                          <Col></Col>
                        </Row>
                        <Row className='mt-4'>
                          <Col>
                            <label className='fw-medium smaller text-primary required'>
                              {t('firstName')}
                            </label>
                            <Form.Control
                              className='w-75 mt-2 border-primary'
                              id='firstName'
                              type='text'
                              value={firstName}
                              onChange={handleFirstName}
                              onKeyUp={handleFirstName}
                              autoComplete='off'
                              required
                              isInvalid={!isFirstNameValid}
                            />
                          </Col>
                          <Col>
                            <label className='fw-medium smaller text-primary required'>
                              {t('lastName')}
                            </label>
                            <Form.Control
                              className='w-75 mt-2 border-primary'
                              id='lastName'
                              type='text'
                              value={lastName}
                              onChange={handleLastName}
                              onKeyUp={handleLastName}
                              autoComplete='off'
                              required
                              isInvalid={!isLastNameValid}
                            />
                          </Col>
                        </Row>
                        <Row className='mt-4'>
                          <Col>
                            <label className='fw-medium smaller text-primary required'>
                              {t('email')}
                            </label>
                            <Form.Control
                              className='w-75 mt-2 border-primary'
                              id='email'
                              type='email'
                              placeholder='name@example.com'
                              value={email}
                              onChange={handleEmail}
                              onKeyUp={handleEmail}
                              readOnly={isMain}
                              autoComplete='on'
                              required
                              isInvalid={!isEmailValid}
                            />
                          </Col>
                          <Col>
                            <label className='fw-medium smaller text-primary required'>
                              {t('phone')}
                            </label>
                            <Form.Control
                              id='phone'
                              type='tel'
                              placeholder={t('telephone')}
                              value={phone}
                              onChange={handlePhone}
                              onKeyUp={handlePhone}
                              autoComplete='off'
                              isInvalid={!isPhoneValid}
                            />
                          </Col>
                        </Row>
                        <Row className='mt-4'>
                          <Col xs={6} md={4} className='mt-3 mt-md-0'>
                            <label htmlFor='isAdmin' className='form-check-label text-primary'>
                              {t('admin')}
                            </label>
                            <input
                              type='checkbox'
                              id='isAdmin'
                              className='form-check-input ms-2'
                              checked={isAdmin}
                              onChange={e => !isMain && setIsAdmin(e.target.checked)}
                            />
                          </Col>
                        </Row>
                        <Row className='mt-4'>
                          <Col>
                            <label className='form-check-label text-primary'>
                              {t('pinCodeOptions')}
                            </label>
                            <label
                              htmlFor='pinCodeEmail'
                              className='form-check-label text-draft ms-3'
                            >
                              {t('email')}
                            </label>
                            <input
                              type='checkbox'
                              className='form-check-input ms-1'
                              id='pinCodeEmail'
                              checked={pinCodeEmail}
                              onChange={handlePincodeEmail}
                            />
                            {/* </Col>
                                                    <Col> */}
                            {/* <label htmlFor='pinCodeSms' className='form-check-label text-draft ms-3'>
                                                            {t('SMS')}
                                                        </label>
                                                        <input
                                                            type='checkbox'
                                                            className='form-check-input ms-1'
                                                            id='pinCodeSms'
                                                            checked={pinCodeSms}
                                                            onChange={handlePincodeSms}
                                                        />
                                                        {!isPincodeOptionsValid && (
                                                            <div className='text-danger mt-2'>
                                                                {t('pincodeOptionNote')}
                                                            </div>
                                                        )} */}
                          </Col>
                        </Row>
                        <Row className='mt-4'>
                          <Col className='text-end'>
                            <Button
                              variant='primary'
                              className='text-white'
                              type='submit'
                              onClick={handleSubmit}
                            >
                              {t('save')} »
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                </Row>

                {microsoftEnabled && (
                  <Row className='mt-4'>
                    <Col lg={4}>
                      <div className='d-flex justify-between section-title-blade'>
                        <div className='pe-4 sm:px-0'>
                          <h6 className='text-draft fw-bold'>{t('microsoft365')}</h6>
                          <p className='mt-1 text-draft'>{t('microsoftConnectionDescription')}</p>
                        </div>
                      </div>
                    </Col>

                    <Col lg={8}>
                      <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                        {microsoftConnected ? (
                          <div>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <div className='fw-bold text-success mb-2'>
                                  {t('microsoftConnected')}
                                </div>

                                {!!microsoftDisplayName && (
                                  <div className='text-draft'>{microsoftDisplayName}</div>
                                )}

                                {!!microsoftUserName && (
                                  <div className='text-draft'>{microsoftUserName}</div>
                                )}

                                {!!microsoftTenantId && (
                                  <div className='text-draft'>
                                    {t('microsoftTenant')}: {microsoftTenantId}
                                  </div>
                                )}
                              </div>

                              <div className='d-flex gap-2'>
                                <Button
                                  variant='outline-primary'
                                  type='button'
                                  onClick={testMicrosoftConnection}
                                >
                                  {t('microsoftTestConnection')}
                                </Button>
                                <Button
                                  variant='outline-danger'
                                  type='button'
                                  onClick={disconnectMicrosoftAccount}
                                >
                                  {t('microsoftDisconnect')}
                                </Button>
                              </div>
                            </div>

                            <hr />

                            <div>
                              <div className='fw-medium smaller text-primary mb-2'>
                                {t('sharePointSite')}
                              </div>

                              {!!sharePointSiteDisplayName && (
                                <div className='text-draft mb-2'>
                                  {t('sharePointSiteConfigured')}: {sharePointSiteDisplayName}
                                </div>
                              )}

                              <div className='d-flex gap-2'>
                                <Form.Control
                                  type='text'
                                  placeholder='https://customer.sharepoint.com/sites/DS-Compliance'
                                  value={sharePointUrl}
                                  onChange={e => setSharePointUrl(e.target.value)}
                                />

                                <Button
                                  variant='primary'
                                  type='button'
                                  disabled={sharePointSiteSaving}
                                  onClick={saveSharePointSiteConfig}
                                >
                                  {t('sharePointSaveSite')}
                                </Button>
                              </div>
                            </div>

                            <hr />

                            <Button
                              variant='outline-secondary'
                              type='button'
                              onClick={connectMicrosoft}
                              disabled={microsoftLoading}
                            >
                              <i className='bi bi-arrow-repeat me-2' />
                              {microsoftLoading
                                ? t('microsoftConnecting')
                                : t('microsoftReconnect')}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant='primary'
                            type='button'
                            onClick={connectMicrosoft}
                            disabled={microsoftLoading}
                          >
                            <img
                              src='/microsoft-logo.svg'
                              alt=''
                              width='20'
                              height='20'
                              className='me-2'
                            />

                            {microsoftLoading ? t('microsoftConnecting') : t('connectMicrosoft365')}
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            </div>

            <hr />
            <div className='col-12'>
              <div className='my-5 md:grid md:grid-cols-3 md:gap-6'>
                <Row>
                  <Col lg={4}>
                    <div className='d-flex justify-between section-title-blade'>
                      <div className='pe-4 sm:px-0'>
                        <h6 className='text-draft fw-bold'>{t('serviceOverview')}</h6>
                        <p className='mt-1 text-draft'>{t('serviceOverviewDescription')}</p>
                      </div>
                    </div>
                  </Col>
                  <Col lg={8}>
                    <div className='px-4 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                      <ServiceOverviewTable />
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            <hr />

            <div className='col-12'>
              <div className='my-5 md:grid md:grid-cols-3 md:gap-6'>
                <Row>
                  <Col lg={4}>
                    <div className='d-flex justify-between section-title-blade'>
                      <div className='pe-4 sm:px-0'>
                        <h6 className='text-draft fw-bold'>{t('twoFactor')}</h6>
                        <p className='mt-1 text-draft'>{t('addAdditional')}</p>
                      </div>
                    </div>
                  </Col>
                  <Col lg={8}>
                    <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                      <h6>{faEnabled ? t('twoFactorEnabled') : t('twoFactorDisabled')}</h6>
                      <p className='mt-3 mb-3 text-draft'>{t('twoFactorDescription')}</p>
                      {faEnabled && (
                        <Col className=' mt-3 text-end'>
                          <Button
                            variant='primary'
                            className='text-white'
                            type='submit'
                            onClick={() => handleChangeTwoFA(false)}
                          >
                            {t('disable')} »
                          </Button>
                        </Col>
                      )}
                      {!faEnabled && (
                        <Col className=' mt-3 text-end'>
                          <Button
                            variant='primary'
                            className='text-white'
                            type='submit'
                            onClick={() => setFaCurrentEnable(!faCurrentEnable)}
                          >
                            {t('enable')} »
                          </Button>
                        </Col>
                      )}
                      {faCurrentEnable && (
                        <>
                          <div className='mt-4 max-w-xl text-sm text-gray-600 txt-wrap'>
                            <p className='font-semibold'>{t('scanQRCode')}</p>
                          </div>
                          {qr && <img src={qr} alt='Scan QR' className='img-fluid my-3' />}
                          <div className='text-start'>
                            <small>{t('manualKey')}</small>
                            <code>{manualKey}</code>
                          </div>
                          <Form.Group className='mt-3'>
                            <Form.Control
                              placeholder={t('6-stelliger Code')}
                              value={code}
                              onChange={e => setCode(e.target.value)}
                            />
                          </Form.Group>

                          <Button className='mt-3' onClick={confirm}>
                            {t('confirm')}
                          </Button>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </div>

            {isMain && (
              <>
                <hr />
                <div className='col-12'>
                  <div className='my-5 md:grid md:grid-cols-3 md:gap-6'>
                    <Row>
                      <Col lg={4}>
                        <div className='d-flex justify-between section-title-blade'>
                          <div className='pe-4 sm:px-0'>
                            <h6 className='text-draft fw-bold'>{t('companyDeleted')}</h6>
                          </div>
                        </div>
                      </Col>
                      <Col lg={8}>
                        <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                          <p className='text-draft mb-3'>{t('companyDeleteWarning')}</p>
                          <div className='text-end'>
                            <Button
                              variant='danger'
                              className='text-white'
                              onClick={() => setConfirmDeleteCompany(true)}
                            >
                              {t('delete')} »
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </>
            )}

            {isMain && (
              <>
                <hr />
                <div className='col-12'>
                  <div className='my-5 md:grid md:grid-cols-3 md:gap-6'>
                    <Row>
                      <Col lg={4}>
                        <div className='d-flex justify-between section-title-blade'>
                          <div className='pe-4 sm:px-0'>
                            <h6 className='text-draft fw-bold'>{t('serviceUsageHistory')}</h6>
                            <p className='mt-1 text-draft'>{t('serviceUsageHistoryDescription')}</p>
                          </div>
                        </div>
                      </Col>
                      <Col lg={8}>
                        <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                          {usageHistory.length === 0 ? (
                            <p className='text-draft'>{t('noUsageHistory')}</p>
                          ) : (
                            <div className='table-responsive'>
                              <table className='table table-sm table-hover'>
                                <thead>
                                  <tr>
                                    <th className='text-primary smaller'>{t('date')}</th>
                                    <th className='text-primary smaller'>{t('service')}</th>
                                    <th className='text-primary smaller'>{t('name')}</th>
                                    <th className='text-primary smaller'>{t('email')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {usageHistory.map((entry, i) => (
                                    <tr key={entry._id ?? i}>
                                      <td className='smaller text-draft'>
                                        {new Date(entry.usedAt).toLocaleString()}
                                      </td>
                                      <td className='smaller text-draft'>
                                        {serviceLabels[entry.service] ?? entry.service}
                                      </td>
                                      <td className='smaller text-draft'>{entry.userName}</td>
                                      <td className='smaller text-draft'>{entry.userEmail}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </>
            )}
          </main>
        </Row>
      </Container>

      <Modal show={showRecoveryCodes} onHide={handleCloseRecoveryCodes} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('enabledTwoFactor')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='fw-bold'>{t('saveRecoveryCode')}</p>
          <p className='small text-muted'>{t('ifLoseAuthDevice')}</p>

          <div className='border p-3 bg-light rounded'>
            <pre className='mb-0' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {recoveryCodes.join('\n')}
            </pre>
          </div>

          <div className='mt-3 d-flex justify-content-center align-items-center gap-2'>
            <Button variant='outline-primary' onClick={copyRecoveryCodes} className='me-2'>
              {t('copyCodes')}
            </Button>
            <Button variant='outline-secondary' onClick={downloadRecoveryCodes}>
              {t('downloadCodes')}
            </Button>
            <Button variant='primary' onClick={handleCloseRecoveryCodes}>
              {t('savedCodes')}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <KConfirm
        show={confirmDeleteCompany}
        message={t('companyDeleteWarning')}
        onConfirm={() => deleteCompany()}
        onCancel={() => setConfirmDeleteCompany(false)}
      />
    </>
  )
}
